import create from 'zustand';
import produce from 'immer';
import { nanoid } from 'nanoid';

import { paymentPlanGenerator } from 'utils/StudyPlan';
import DayJs from 'utils/Dayjs';

const STUDENT_PAYMENT_INITIAL = {
	grades: [],
	payments: [],
	generator: {
		price: '',
		gradeId: '',
		sections: 9,
		payments: []
	},
	edited: false
};

export const useStudentPaymentStore = create(set => ({
	...STUDENT_PAYMENT_INITIAL,

	// Handle incoming payment addition
	onIncomingPayment: (data) => set(produce(state => {
		// Create temporary amount store
		let { amount } = data;

		// Loop through payments and update paid amount
		state.payments = state.payments.map(payment => {
			// Do not update payment if amount is zero
			if (amount === 0 || payment.state === 'executed') return payment;

			// Calculate remainding amount
			const remainding = payment.amount - payment.paid;

			// Check if amount is bigger than amount
			if (amount - remainding > 0) {
				// Update amount
				amount -= remainding;

				// return executed payment
				return {
					...payment,
					state: 'executed',
					paid: payment.amount,
					transactionAt: DayJs().toISOString()
				};
			} else {
				// Calculate paid amount
				const paid = payment.paid + amount;

				// Update partial payment
				const temp = {
					...payment,
					paid: payment.paid + amount,
					state: paid === payment.amount ? 'executed' : 'partial',
					transactionAt: DayJs().toISOString()
				};

				// Update amount
				amount = 0;
				
				// Return updated payment
				return temp;
			}
		});
	})),

	// Handle student's payment addition
	onAddPayment: (gradeId, payment) => set(produce(state => {
		state.payments.push({
			key: nanoid(),
			gradeId: gradeId,
			type: payment.type,
			state: payment.state,
			amount: payment.amount,
			paid: 0,
			deadlineAt: payment.deadlineAt
		});
		state.edited = true;
	})),

	// Handle student's payment removal
	onRemovePayment: (paymentKey) => set(produce(state => {
		state.payments = state.payments.filter(p => p.key !== paymentKey);
		state.edited = true;
	})),

	// Handle student's payment state change
	onChangePayment: (paymentKey, { name, value }) => set(produce(state => {
		const payment = state.payments.find(p => p.key === paymentKey);
		payment[name] = value;
		state.edited = true;
	})),

	// Handle generator init
	onInitGenerator: ({ id, price }) => set(produce(state => {
		state.generator.gradeId = id;
		state.generator.price = price;
	})),

	// Handle generators reset
	onResetGenerator: () => set(produce(state => {
		state.generator = {
			price: '',
			gradeId: '',
			sections: 9,
			payments: []
		};
	})),

	// Handle generator change
	onChangeGenerator: ({ name, value }) => set(produce(state => {
		state.generator[name] = value;
	})),

	// Handle student's payment generation
	onGeneratePayments: () => set(produce(state => {
		const grade = state.grades.find(g => g.id === state.generator.gradeId);
		state.generator.payments = paymentPlanGenerator(grade, state.generator);
	})),

	// Handle generated payment change
	onChangeGeneratedPayment: (paymentKey, { name, value }) => set(produce(state => {
		const payment = state.generator.payments.find(p => p.key === paymentKey);
		payment[name] = value;
	})),

	// Handle generated payment removal
	onRemoveGeneratedPayment: (paymentKey) => set(produce(state => {
		state.generator.payments = state.generator.payments.filter(p => p.key !== paymentKey);
	})),

	// Handle generated payment addition
	onAddGeneratedPayments: () => set(produce(state => {
		state.payments = [...state.payments, ...state.generator.payments];
		state.generator = {
			price: '',
			gradeId: '',
			sections: 9,
			payments: []
		};
		state.edited = true;
	})),

	// Initialize student's payment state
	onInit: ({ grades, payments }) => set(produce(state => {
		state.edited = false;
		state.grades = grades;
		state.payments = payments.map(payment => ({
			key: nanoid(),
			...payment
		}));
		const active = state.grades.find(g => g.state === 'active');
		const rest = state.grades.filter(g => g.state !== 'active');
		state.grades = active ? [active, ...rest] : state.grades;
	})),

	// Reset store to initial state
	onReset: () => set({
		...STUDENT_PAYMENT_INITIAL
	})
}));